import { InfoCircleOutlined } from '@ant-design/icons';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { getProfilePics } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import { Col, Row } from 'antd';
import { isEmpty, pick, pickBy } from 'lodash';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { DashboardRecentActivityIcon } from '../../icons';
import {
  BiometricBloodPressureCard,
  BiometricGlucoseCard,
  BiometricPulseOximeterCard,
  BiometricTemperatureCard,
  BiometricWeightCard,
  EngagementCard,
  MotionCard,
  RecentCallCard,
  SmartHomeDoorWindowCard,
  SmartHomeEnvironmentalCard,
  SmartHomeOccupancyCard,
  SmartHomeSmartPlugCard,
  SmartHomeToiletFlushCard,
} from './cards';
import { useFetchRecentActivityData } from './hooks';
import './RecentActivity.less';
import { CATEGORY_CARD_MAPPING, RECENT_ACTIVITY_CARD_TYPES } from './RecentActivityUtils';
import { summarizeRecentActivityForReport, useRegisterReportSection } from '../../reportUtils';

const StyledRow = styled(Row).attrs(() => ({ gutter: [16, 16] }))``;

const StyledCol = styled(Col).attrs(() => ({ xs: 24, sm: 24, md: 12, lg: 8 }))``;

const CardComponents = {
  [RECENT_ACTIVITY_CARD_TYPES.MOTION]: MotionCard,
  [RECENT_ACTIVITY_CARD_TYPES.RECENT_CALLS]: RecentCallCard,
  [RECENT_ACTIVITY_CARD_TYPES.BLOOD_PRESSURE]: BiometricBloodPressureCard,
  [RECENT_ACTIVITY_CARD_TYPES.THERMOMETER]: BiometricTemperatureCard,
  [RECENT_ACTIVITY_CARD_TYPES.GLUCOSE_METER]: BiometricGlucoseCard,
  [RECENT_ACTIVITY_CARD_TYPES.PULSE_OXIMETER]: BiometricPulseOximeterCard,
  [RECENT_ACTIVITY_CARD_TYPES.WEIGHT_SCALE]: BiometricWeightCard,
  [RECENT_ACTIVITY_CARD_TYPES.DOOR_WINDOW_SENSOR]: SmartHomeDoorWindowCard,
  [RECENT_ACTIVITY_CARD_TYPES.ENVIRONMENTAL_SENSOR]: SmartHomeEnvironmentalCard,
  [RECENT_ACTIVITY_CARD_TYPES.OCCUPANCY_SENSOR]: SmartHomeOccupancyCard,
  [RECENT_ACTIVITY_CARD_TYPES.SMART_PLUG]: SmartHomeSmartPlugCard,
  [RECENT_ACTIVITY_CARD_TYPES.TOILET_FLUSH_SENSOR]: SmartHomeToiletFlushCard,
  [RECENT_ACTIVITY_CARD_TYPES.ENGAGEMENT]: EngagementCard,
};

const NoActivityContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  border: 1px solid #d9ecfa;
  border-radius: 18px;
  background-color: #ffffff;
`;

const NoActivityIcon = styled(InfoCircleOutlined)`
  font-size: 20px;
  color: #3fa9f5;
  flex-shrink: 0;
`;

const NoActivityText = styled.p`
  margin: 0;
  font-size: 14px;
  color: #0a0a0a;
  white-space: nowrap;
`;

const NoRecentActivity = () => {
  const trans = useSelector(translator);
  return (
    <NoActivityContainer>
      <NoActivityIcon />
      <NoActivityText>{trans('NO_RECENT_ACTIVITY')}</NoActivityText>
    </NoActivityContainer>
  );
};

NoRecentActivity.propTypes = {};

const RecentActivity = (props) => {
  const { supportedCategories } = props;
  const trans = useSelector(translator);
  const resourcesMap = useSelector(getProfilePics());

  const filteredCardComponents = useMemo(() => {
    const supportedCards = Object.values(pick(CATEGORY_CARD_MAPPING, supportedCategories)).flat();
    return pickBy(CardComponents, (_, key) => {
      if (
        [RECENT_ACTIVITY_CARD_TYPES.RECENT_CALLS, RECENT_ACTIVITY_CARD_TYPES.ENGAGEMENT].includes(
          key
        )
      ) {
        return true;
      }
      return supportedCards.includes(key);
    });
  }, [supportedCategories]);

  const { recentActivityData } = useFetchRecentActivityData({ ...props, supportedCategories });

  const reportRows = useMemo(
    () =>
      summarizeRecentActivityForReport(recentActivityData, {
        trans,
        timezone: props.timezone,
        is24HourFormat: props.is24HourFormat,
        locale: props.locale,
        measurementSystem: props.measurementSystem,
        useruuid: props.useruuid,
        resourcesMap,
      }),
    [
      recentActivityData,
      trans,
      props.timezone,
      props.is24HourFormat,
      props.locale,
      props.measurementSystem,
      props.useruuid,
      resourcesMap,
    ]
  );
  useRegisterReportSection('recentActivity', reportRows, [reportRows]);

  const noDataAvailable = useMemo(() => {
    const anyLoading = Object.values(recentActivityData).some((card) => card.isLoading);
    if (anyLoading) return false;

    return Object.values(recentActivityData).every((card) => isEmpty(card.data));
  }, [recentActivityData]);

  return (
    <>
      <div className="recent-activity-section">
        <div className="section-header">
          <DashboardRecentActivityIcon className="recent-activity-header-icon" />
          <h3 className="section-title">{trans('RECENT_ACTIVITY')}</h3>
        </div>
        {noDataAvailable ? (
          <NoRecentActivity />
        ) : (
          <StyledRow>
            {Object.entries(filteredCardComponents).map(([key, Card]) => {
              const dataProps = recentActivityData[key] || {};
              if (!dataProps.isLoading && isEmpty(dataProps.data)) return null;
              return (
                <StyledCol key={key}>
                  <Card {...props} {...dataProps} />
                </StyledCol>
              );
            })}
          </StyledRow>
        )}
      </div>
    </>
  );
};

RecentActivity.propTypes = {
  useruuid: PropTypes.string,
  timezone: PropTypes.string,
  is24HourFormat: PropTypes.bool,
  locale: PropTypes.string,
  measurementSystem: PropTypes.string,
  accessories: PropTypes.arrayOf(PropTypes.object),
  supportedCategories: PropTypes.array,
};

export default RecentActivity;
